
import React, { useState, useEffect } from 'react';
import { useForm } from '../../context/FormContext';
import { useToast } from "../../hooks/use-toast";
import ConfirmationDialog from './components/ConfirmationDialog';
import SubmitConfirmationDialog from './components/SubmitConfirmationDialog';
import PageNavigation from './components/PageNavigation';
import ReviewPage from './page-components/ReviewPage';
import { 
  ValidationSection, 
  FormAlertSection, 
  FormContentRenderer, 
  useFormValidation,
  useElementGroups
} from './page-components';

interface FormPageProps {
  pageNumber: number;
  hasWorksheets?: boolean;
  documentUrls?: string[];
}

const FormPage: React.FC<FormPageProps> = ({ 
  pageNumber, 
  hasWorksheets = false, 
  documentUrls = [] 
}) => {
  const { 
    formConfig, 
    nextPage, 
    prevPage, 
    currentPage, 
    totalPages, 
    submitForm, 
    isSubmitting,
    formData,
  } = useForm();
  
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isSubmitConfirmDialogOpen, setIsSubmitConfirmDialogOpen] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const { toast } = useToast();
  const { validationErrors, validateRequiredFields, showValidationErrorToast } = useFormValidation();
  
  const currentPageConfig = formConfig.find((page) => page.page === pageNumber);
  
  if (!currentPageConfig) {
    return <div>Page not found</div>;
  }

  // Check if student is absent and we're on page 2
  const isStudentAbsent = formData.learning_status === "น้องไม่เข้าเรียน (ลากะทันหัน)";
  const isPage2 = pageNumber === 2;

  // Check if terms are agreed to on page 1
  const isPage1 = pageNumber === 1;
  const isPage3 = pageNumber === 3;
  const isPage4 = pageNumber === 4;
  const isTermsAccepted = formData.agreed_to_terms === true;
  
  // Check if Next button should be disabled
  const isNextButtonDisabled = isPage1 && !isTermsAccepted;
  
  const handleNextPage = () => {
    // For any page, validate all required fields
    const isValid = validateRequiredFields(currentPageConfig.elements, formData);
    if (!isValid) {
      showValidationErrorToast();
      return;
    }
    
    // Check if we're on the last page and need to show the review page
    if (currentPage === totalPages && !isReviewMode) {
      setIsReviewMode(true);
    } else if (isReviewMode) {
      // If we're already in review mode, show the final confirmation dialog
      setIsSubmitConfirmDialogOpen(true);
    } else {
      nextPage();
    }
  };

  const handlePrevPage = () => {
    if (isReviewMode) {
      setIsReviewMode(false);
    } else {
      prevPage();
    }
  };

  const handleSubmitClick = () => {
    if (isPage2 && isStudentAbsent) {
      // Even when student is absent, validate required fields before showing confirm dialog
      const isValid = validateRequiredFields(currentPageConfig.elements, formData);
      if (!isValid) {
        showValidationErrorToast();
        return;
      }
      
      setIsConfirmDialogOpen(true);
    } else {
      handleNextPage();
    }
  };

  const handleConfirmedSubmit = () => {
    setIsConfirmDialogOpen(false);
    submitForm();
  };

  const handleFinalSubmit = () => {
    setIsSubmitConfirmDialogOpen(false);
    submitForm();
  };

  // Get element groups for Page 3
  const elementGroups = useElementGroups(isPage3, currentPageConfig.elements);

  // Show toast notification when form has saved data on initial load
  useEffect(() => {
    if (Object.keys(formData).length > 0 && pageNumber === 1) {
      toast({
        title: "ข้อมูลถูกบันทึกไว้",
        description: "กำลังแสดงข้อมูลที่คุณกรอกไว้ก่อนหน้านี้",
        duration: 5000,
      });
    }
  }, []);

  // Filter the elements for page 4 based on whether we have worksheets
  let pageElements = currentPageConfig.elements;
  
  if (isPage4) {
    if (hasWorksheets) {
      // Show document links instead of file upload
      pageElements = pageElements.filter(element => element.id !== 'worksheet_upload');
    } else {
      // Hide document links
      pageElements = pageElements.filter(element => element.id !== 'worksheet_links');
    }
  }

  // Create a modified page config with the filtered elements
  const modifiedPageConfig = isPage4 
    ? {...currentPageConfig, elements: pageElements} 
    : currentPageConfig;

  return (
    <div className="max-w-3xl mx-auto">
      <ValidationSection errors={validationErrors} />
      
      {isReviewMode ? (
        <ReviewPage />
      ) : (
        <FormContentRenderer
          isPage3={isPage3}
          currentPageConfig={modifiedPageConfig}
          elementGroups={elementGroups}
          documentUrls={documentUrls}
        />
      )}
      
      <PageNavigation 
        pageNumber={isReviewMode ? totalPages + 1 : pageNumber}
        totalPages={totalPages + 1} // +1 to account for review page
        isNextButtonDisabled={isNextButtonDisabled}
        isSubmitting={isSubmitting}
        onPrevPage={handlePrevPage}
        onNextClick={handleSubmitClick}
        isStudentAbsent={isStudentAbsent}
        isReviewMode={isReviewMode}
      />
      
      {!isReviewMode && (
        <FormAlertSection
          isPage1={isPage1}
          isPage2={isPage2}
          isTermsAccepted={isTermsAccepted}
          isStudentAbsent={isStudentAbsent}
        />
      )}
      
      <ConfirmationDialog 
        isOpen={isConfirmDialogOpen} 
        onOpenChange={setIsConfirmDialogOpen} 
        onConfirm={handleConfirmedSubmit} 
      />

      <SubmitConfirmationDialog
        isOpen={isSubmitConfirmDialogOpen}
        onOpenChange={setIsSubmitConfirmDialogOpen}
        onConfirm={handleFinalSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default FormPage;
